import type { StrongsEntry } from '../types/strongs'
import type { Frame } from '../types/frame'
import type { MapPlace, MapPlaceDetail } from '../types/map'
import type { SearchRequest, SearchResponse } from '../types/search'
import type { ApocryphaSearchRequest, ApocryphaSearchResponse, ApocryphaBook } from '../types/apocrypha'

const API_BASE_URL = 'https://api.psro.net/api'

// In-flight CSRF token fetch promise deduplication
let csrfFetchPromise: Promise<string | null> | null = null

async function ensureCsrfToken(userStore: ReturnType<typeof import('../stores/userStore').useUserStore>): Promise<string | null> {
  if (userStore.csrfToken) return userStore.csrfToken

  if (!csrfFetchPromise) {
    csrfFetchPromise = fetch(`${API_BASE_URL}/csrf-token`, {
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((json) => {
        const token = json?.data?.csrfToken ?? null
        if (token) userStore.csrfToken = token
        return token
      })
      .catch(() => null)
      .finally(() => {
        csrfFetchPromise = null
      })
  }

  return csrfFetchPromise
}

export interface ApiResponse<T = any> {
  success: boolean
  data?: T
  error?: string
  meta?: {
    total?: number
    page?: number
    limit?: number
  }
}

export interface BibleVerse {
  book: string
  book_eng: string
  chapter: number
  verse: number
  text: string
}

export interface BibleBook {
  id: number
  eng_short: string
  eng_full: string
  chi_short: string
  chi_full: string
}

export interface Snapshot {
  id: number
  snapshot_name: string
  created_at: string
  relative_time?: string
  frame_count?: number
}

class APIClient {
  private baseUrl: string

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`
    const defaultHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    // 對於 POST/PUT/PATCH/DELETE，添加 X-CSRF-Token header
    const isMutation = options.method && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(options.method)
    if (isMutation) {
      const { useUserStore } = await import('../stores/userStore')
      const userStore = useUserStore()
      const token = userStore.csrfToken || (await ensureCsrfToken(userStore))
      if (token) {
        defaultHeaders['X-CSRF-Token'] = token
      }
    }

    const response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
      signal: options.signal,
    })

    let data: any
    const contentType = response.headers.get('content-type')
    if (contentType?.includes('application/json')) {
      data = await response.json()
    } else {
      const text = await response.text()
      throw new Error(`Expected JSON but got ${contentType || 'unknown'}: ${text.slice(0, 100)}`)
    }

    if (!response.ok) {
      throw new Error(data.error || `HTTP ${response.status}`)
    }

    return data
  }

  // Frame endpoints
  async getFrames(): Promise<Frame[]> {
    const response = await this.request<Frame[]>('/frames')
    const frames = response.data || []
    // 確保所有 frames 都有 type 字段（舊資料可能缺少）
    return frames.map(f => ({
      ...f,
      type: (f as any).type || 'bible-reading',
    }))
  }

  async getFrame(frameId: number): Promise<Frame | null> {
    try {
      const response = await this.request<Frame>(`/frames/${frameId}`)
      return response.data || null
    } catch {
      return null
    }
  }

  async createFrame(data: Omit<Frame, 'id' | 'created_at' | 'updated_at'>): Promise<Frame> {
    const response = await this.request<Frame>('/frames', {
      method: 'POST',
      body: JSON.stringify(data),
    })
    return response.data!
  }

  async updateFrame(frameId: number, data: Partial<Frame>): Promise<Frame> {
    const response = await this.request<Frame>(`/frames/${frameId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
    return response.data!
  }

  async deleteFrame(frameId: number): Promise<void> {
    await this.request(`/frames/${frameId}`, {
      method: 'DELETE',
    })
  }

  async saveFramesState(frames: Frame[]): Promise<Frame[]> {
    const response = await this.request<Frame[]>('/frames/batch/save', {
      method: 'POST',
      body: JSON.stringify({ frames }),
    })
    return response.data || []
  }

  // Bible endpoints
  async getBooks(versionCode?: string): Promise<BibleBook[]> {
    const params = versionCode ? `?version=${encodeURIComponent(versionCode)}` : ''
    const response = await this.request<any>(`/bible/books${params}`)
    return response.data?.books || response.data || []
  }

  async getOTBooks(): Promise<BibleBook[]> {
    const response = await this.request<any>('/bible/books/ot')
    return response.data?.books || response.data || []
  }

  async getNTBooks(): Promise<BibleBook[]> {
    const response = await this.request<any>('/bible/books/nt')
    return response.data?.books || response.data || []
  }

  async searchBible(
    versionCode: string,
    query: string,
    isRegex = false,
    page = 0,
    limit = 100
  ): Promise<SearchResponse> {
    const response = await this.request<SearchResponse['data']>('/bible/search', {
      method: 'POST',
      body: JSON.stringify({
        query,
        versionCode,
        isRegex,
        page,
        limit,
      }),
    })
    return {
      success: response.success,
      data: response.data,
      error: response.error,
    }
  }

  async getVerse(book: string, chapter: number, verse: number): Promise<any> {
    const response = await this.request<any>(`/bible/verse/${encodeURIComponent(book)}/${chapter}/${verse}`)
    return response.data
  }

  async getChapter(book: string, chapter: number, version?: string, includeStrong?: boolean): Promise<any> {
    const queryParts = []
    if (version) queryParts.push(`version=${encodeURIComponent(version)}`)
    if (includeStrong) queryParts.push('includeStrong=true')
    const params = queryParts.length > 0 ? `?${queryParts.join('&')}` : ''
    const response = await this.request<any>(`/bible/chapter/${encodeURIComponent(book)}/${chapter}${params}`)
    return response.data
  }

  async getVersions(): Promise<any[]> {
    const response = await this.request<any[]>('/bible/versions')
    // 返回完整列表，前端負責渲染和捲動
    return response.data || []
  }

  async validateVersion(code: string): Promise<boolean> {
    try {
      const response = await this.request<{ code: string; isAvailable: boolean }>(
        `/bible/versions/${code}/validate`
      )
      return response.data?.isAvailable || false
    } catch {
      return false
    }
  }

  // Snapshot endpoints
  async saveSnapshot(frames: Frame[]): Promise<Snapshot> {
    const response = await this.request<Snapshot>('/snapshots', {
      method: 'POST',
      body: JSON.stringify({ frames }),
    })
    return response.data!
  }

  async getSnapshots(): Promise<Snapshot[]> {
    const response = await this.request<Snapshot[]>('/snapshots')
    return response.data || []
  }

  async restoreSnapshot(snapshotId: number): Promise<Frame[]> {
    const response = await this.request<Frame[]>(`/snapshots/${snapshotId}/restore`, {
      method: 'POST',
    })
    return response.data || []
  }

  async deleteSnapshot(snapshotId: number): Promise<void> {
    await this.request(`/snapshots/${snapshotId}`, {
      method: 'DELETE',
    })
  }

  async deleteAllSnapshots(): Promise<void> {
    await this.request('/snapshots', {
      method: 'DELETE',
    })
  }

  // Bookmark endpoints
  async getAllBookmarks(): Promise<any[]> {
    const response = await this.request<any[]>('/bookmarks')
    return response.data || []
  }

  async createBookmark(data: {
    versionCode: string
    bookId: number
    chapter: number
    verses: number[]
    bookName: string
    versionName: string
  }): Promise<any> {
    const response = await this.request<any>('/bookmarks', {
      method: 'POST',
      body: JSON.stringify(data),
    })
    if (!response.success) {
      throw new Error(response.error || 'Failed to create bookmark')
    }
    return response.data!
  }

  async deleteBookmark(bookmarkId: number): Promise<void> {
    const response = await this.request<any>(`/bookmarks/${bookmarkId}`, {
      method: 'DELETE',
    })
    if (!response.success) {
      throw new Error(response.error || 'Failed to delete bookmark')
    }
  }

  // Note endpoints
  async getAllNotes(): Promise<any[]> {
    const response = await this.request<any[]>('/notes')
    return response.data || []
  }

  async getNoteById(noteId: number): Promise<any> {
    const response = await this.request<any>(`/notes/${noteId}`)
    return response.data || null
  }

  async getNotesByFrame(frameId: number): Promise<any[]> {
    const response = await this.request<any[]>(`/notes/frame/${frameId}`)
    return response.data || []
  }

  async getNotesByVerse(versionCode: string, bookId: number, chapterNumber: number): Promise<any[]> {
    const response = await this.request<any[]>(`/notes/verse/${versionCode}/${bookId}/${chapterNumber}`)
    return response.data || []
  }

  async createNote(data: {
    frameId?: number
    versionCode: string
    bookId: number
    chapterNumber: number
    verseStart?: number
    content?: string
  }): Promise<any> {
    const response = await this.request<any>('/notes', {
      method: 'POST',
      body: JSON.stringify(data),
    })
    if (!response.success) {
      throw new Error(response.error || 'Failed to create note')
    }
    return response.data!
  }

  async updateNote(noteId: number, data: { content: string }): Promise<any> {
    const response = await this.request<any>(`/notes/${noteId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
    if (!response.success) {
      throw new Error(response.error || 'Failed to update note')
    }
    return response.data!
  }

  async deleteNote(noteId: number): Promise<void> {
    const response = await this.request<any>(`/notes/${noteId}`, {
      method: 'DELETE',
    })
    if (!response.success) {
      throw new Error(response.error || 'Failed to delete note')
    }
  }

  async lookupStrongs(number: string, testament: 'OT' | 'NT' = 'OT'): Promise<StrongsEntry> {
    const response = await this.request<StrongsEntry>(`/bible/strongs/${encodeURIComponent(number)}?testament=${testament}`)
    if (!response.success || !response.data) {
      throw new Error(response.error || `Failed to lookup Strong's #${number}`)
    }
    return response.data
  }

  async getChapterPlaces(bookId: number, chapter: number): Promise<MapPlace[]> {
    const response = await this.request<MapPlace[]>(`/map/chapter/${bookId}/${chapter}`)
    if (!response.success) {
      throw new Error(response.error || `Failed to fetch places for ${bookId}:${chapter}`)
    }
    return response.data || []
  }

  async getVersePlaces(bookId: number, chapter: number, verse: number): Promise<MapPlace[]> {
    const response = await this.request<MapPlace[]>(`/map/verse/${bookId}/${chapter}/${verse}`)
    if (!response.success) {
      throw new Error(response.error || `Failed to fetch places for ${bookId}:${chapter}:${verse}`)
    }
    return response.data || []
  }

  async getPlaceDetail(placeId: string): Promise<MapPlaceDetail | null> {
    const response = await this.request<MapPlaceDetail>(`/map/place/${encodeURIComponent(placeId)}`)
    if (!response.success) {
      throw new Error(response.error || `Failed to fetch place ${placeId}`)
    }
    return response.data || null
  }

  // Auth endpoints
  async getMe(): Promise<{ user: any; csrfToken: string } | null> {
    try {
      const response = await this.request<{ user: any; csrfToken: string } | null>('/auth/me')
      if (!response.success) {
        return null
      }
      // 後端返回 success: true 且 data 可能為 null（無 session）
      return response.data || null
    } catch {
      return null
    }
  }

  async getUserProfile(userId: number): Promise<any> {
    try {
      const response = await this.request<any>(`/users/${userId}`)
      return response.data || null
    } catch {
      return null
    }
  }

  async updateUserProfile(userId: number, data: { nickname?: string }): Promise<any> {
    try {
      const response = await this.request<any>(`/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      })
      return response.data || null
    } catch {
      return null
    }
  }

  // Keyword search endpoints
  async keywordSearch(request: SearchRequest): Promise<SearchResponse> {
    const response = await this.request<SearchResponse['data']>('/bible/search', {
      method: 'POST',
      body: JSON.stringify(request),
    })
    return {
      success: response.success,
      data: response.data,
      error: response.error,
    }
  }

  // Apocrypha endpoints
  async getApocryphaBooks(): Promise<{ success: boolean; data?: ApocryphaBook[]; error?: string }> {
    const response = await this.request<ApocryphaBook[]>('/bible/apocrypha/books', {
      method: 'GET',
    })
    return {
      success: response.success,
      data: response.data,
      error: response.error,
    }
  }

  async searchApocrypha(request: ApocryphaSearchRequest): Promise<ApocryphaSearchResponse> {
    const response = await this.request<ApocryphaSearchResponse['data']>('/bible/apocrypha/search', {
      method: 'POST',
      body: JSON.stringify(request),
    })
    return {
      success: response.success,
      data: response.data,
      error: response.error,
    }
  }
}

export const apiClient = new APIClient()
